// Définir l'ordre des onglets dans chaque workshop pour la navigation
export const TAB_ORDER = {
  workshop1: ['dashboard', 'context', 'business-values', 'events', 'security', 'security-controls', 'risk-treatment'],
  workshop2: ['atelier2-activite1', 'atelier2-activite2', 'atelier2-activite3'],
  workshop3: ['atelier3-activite1', 'atelier3-activite2', 'atelier3-activite3', 'atelier3-activite4', 'atelier3-activite5'],
  workshop4: ['atelier4-threat-intelligence', 'atelier4-cti-scenarios'],
  workshop5: ['atelier5-synthese-scenarios', 'atelier5-implementation-checklist'],
  fusion: ['atelier5-fusion-ptr'],


  // Vous pouvez ajouter d'autres workshops au fur et à mesure que vous les implémentez
};

// Déterminer le workshop actif en fonction de l'onglet sélectionné
export const getActiveWorkshop = (activeTab) => {
  for (const [workshop, tabs] of Object.entries(TAB_ORDER)) {
    if (tabs.includes(activeTab)) {
      return workshop;
    }
  }
  return 'workshop1'; // Valeur par défaut
};

// Fonction pour naviguer vers l'onglet précédent
export const getPreviousTab = (activeTab, activeWorkshop) => {
  const currentWorkshopTabs = TAB_ORDER[activeWorkshop] || [];
  const currentIndex = currentWorkshopTabs.indexOf(activeTab);

  if (currentIndex > 0) {
    // Aller à l'onglet précédent dans le même workshop
    return {
      nextTab: currentWorkshopTabs[currentIndex - 1],
      nextWorkshop: activeWorkshop
    };
  } else if (activeWorkshop !== 'workshop1') {
    // Si nous sommes au premier onglet d'un workshop (pas le premier workshop),
    // aller au dernier onglet du workshop précédent

    // Trouver le workshop précédent
    const workshopKeys = Object.keys(TAB_ORDER);
    const currentWorkshopIndex = workshopKeys.indexOf(activeWorkshop);

    if (currentWorkshopIndex > 0) {
      const previousWorkshop = workshopKeys[currentWorkshopIndex - 1];
      const previousWorkshopTabs = TAB_ORDER[previousWorkshop] || [];

      // Aller au dernier onglet du workshop précédent
      return {
        nextTab: previousWorkshopTabs[previousWorkshopTabs.length - 1],
        nextWorkshop: previousWorkshop
      };
    }
  }

  // Rester sur le même onglet si nous sommes déjà au début
  return {
    nextTab: activeTab,
    nextWorkshop: activeWorkshop
  };
};

// Fonction pour naviguer vers l'onglet suivant
export const getNextTab = (activeTab, activeWorkshop) => {
  const currentWorkshopTabs = TAB_ORDER[activeWorkshop] || [];
  const currentIndex = currentWorkshopTabs.indexOf(activeTab);

  if (currentIndex < currentWorkshopTabs.length - 1) {
    // Aller à l'onglet suivant dans le même workshop
    return {
      nextTab: currentWorkshopTabs[currentIndex + 1],
      nextWorkshop: activeWorkshop
    };
  } else {
    // Si nous sommes au dernier onglet d'un workshop,
    // aller au premier onglet du workshop suivant

    // Trouver le workshop suivant
    const workshopKeys = Object.keys(TAB_ORDER);
    const currentWorkshopIndex = workshopKeys.indexOf(activeWorkshop);

    if (currentWorkshopIndex < workshopKeys.length - 1) {
      const nextWorkshop = workshopKeys[currentWorkshopIndex + 1];
      const nextWorkshopTabs = TAB_ORDER[nextWorkshop] || [];

      // Aller au premier onglet du workshop suivant
      if (nextWorkshopTabs.length > 0) {
        return {
          nextTab: nextWorkshopTabs[0],
          nextWorkshop: nextWorkshop
        };
      }
    }
  }

  // Rester sur le même onglet si nous sommes déjà à la fin
  return {
    nextTab: activeTab,
    nextWorkshop: activeWorkshop
  };
};

// Déterminer si c'est le premier onglet global
export const isFirstTab = (activeTab) => {
  const firstWorkshop = Object.keys(TAB_ORDER)[0];
  const firstTab = TAB_ORDER[firstWorkshop][0];
  return activeTab === firstTab;
};

// Déterminer si c'est le dernier onglet global
export const isLastTab = (activeTab) => {
  const lastWorkshop = Object.keys(TAB_ORDER)[Object.keys(TAB_ORDER).length - 1];
  const lastWorkshopTabs = TAB_ORDER[lastWorkshop];
  const lastTab = lastWorkshopTabs[lastWorkshopTabs.length - 1];
  return activeTab === lastTab;
};

// Variable globale pour stocker l'état des modifications non enregistrées
let _hasUnsavedChanges = false;
let _navigationWarningCallback = null;
let _navigationWarningDialogOpen = false;

// Fonction pour définir l'état des modifications non enregistrées
export const setUnsavedChanges = (hasUnsavedChanges) => {
  _hasUnsavedChanges = hasUnsavedChanges;
};

// Fonction pour obtenir l'état des modifications non enregistrées
export const getUnsavedChanges = () => {
  return _hasUnsavedChanges;
};

// Fonction pour définir le callback de navigation
export const setNavigationWarningCallback = (callback) => {
  _navigationWarningCallback = callback;
};

// Fonction pour vérifier si la navigation est autorisée
export const checkNavigationAllowed = () => {
  if (_hasUnsavedChanges && _navigationWarningCallback) {
    return _navigationWarningCallback();
  }
  return true;
};

// Fonction pour définir l'état de la boîte de dialogue d'avertissement
export const setNavigationWarningDialogOpen = (isOpen) => {
  _navigationWarningDialogOpen = isOpen;
};

// Fonction pour obtenir l'état de la boîte de dialogue d'avertissement
export const getNavigationWarningDialogOpen = () => {
  return _navigationWarningDialogOpen;
};

// Liste ordonnée de tous les onglets pour la progression (hors dashboard)
export const WORKSHOP_TABS_IN_ORDER = Object.values(TAB_ORDER).flat().filter(tab => tab !== 'dashboard');

// Obtenir la clé locale pour la progression d'une analyse spécifique
const getProgressionStorageKey = (analysisId) => {
  return analysisId ? `ebiosrm_progression_${analysisId}` : 'ebiosrm_progression_local';
};

// Récupérer les onglets complétés (sauvegardés)
export const getCompletedTabs = (analysisId) => {
  try {
    const key = getProgressionStorageKey(analysisId);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error('Error reading completed tabs', e);
    return [];
  }
};

// Marquer un onglet comme complété suite à une sauvegarde
export const markTabAsCompleted = (tabId, analysisId) => {
  try {
    if (!tabId || tabId === 'dashboard') return;

    const completedTabs = getCompletedTabs(analysisId);
    if (!completedTabs.includes(tabId)) {
      completedTabs.push(tabId);
      const key = getProgressionStorageKey(analysisId);
      localStorage.setItem(key, JSON.stringify(completedTabs));

      // Déclencher un événement pour que l'interface nav mette à jour ses pastilles
      window.dispatchEvent(new CustomEvent('ebiosrm_progression_updated', {
        detail: { tabId, analysisId, completedTabs }
      }));
    }
  } catch (e) {
    console.error('Error saving completed tab', e);
  }
};

// Helper pour vérifier si un onglet est complété (inclut les règles métiers spéciales)
export const isTabCompleted = (tabId, completedTabs) => {
  if (!completedTabs || !Array.isArray(completedTabs)) return false;
  if (completedTabs.includes(tabId)) return true;

  // Cas spécial : 'risk-treatment' (Plan de Traitement)
  // Il est sans bouton "sauvegarder", donc complété automatiquement si les 5 activités 
  // précédentes de l'atelier 1 sont déjà complétées.
  if (tabId === 'risk-treatment') {
    const prerequisites = ['context', 'business-values', 'events', 'security', 'security-controls'];
    return prerequisites.every(id => completedTabs.includes(id));
  }

  return false;
};

// Obtenir l'index le plus élevé parmi les onglets complétés
export const getHighestCompletedTabIndex = (completedTabs) => {
  let highestIndex = -1;
  if (!completedTabs || !Array.isArray(completedTabs)) return highestIndex;

  WORKSHOP_TABS_IN_ORDER.forEach((tab, idx) => {
    if (isTabCompleted(tab, completedTabs)) {
      highestIndex = idx;
    }
  });

  return highestIndex;
};

/**
 * Determine the progression status of a tab
 * @param {string} tabId The tab to check
 * @param {Array<string>} completedTabs Array of completed tab IDs
 * @returns {'completed' | 'next' | 'pending'} The progression status
 */
export const getTabProgressionStatus = (tabId, completedTabs) => {
  if (isTabCompleted(tabId, completedTabs)) {
    return 'completed';
  }

  const tabIndex = WORKSHOP_TABS_IN_ORDER.indexOf(tabId);
  if (tabIndex === -1) return 'pending';

  // C'est un onglet "next" (jaune) s'il s'agit du tout premier onglet global et qu'il n'est pas complété
  if (tabIndex === 0) {
    return 'next';
  }

  // C'est un onglet "next" (jaune) si l'onglet qui le précède DIRECTEMENT est déjà complété
  const prevTabId = WORKSHOP_TABS_IN_ORDER[tabIndex - 1];
  if (isTabCompleted(prevTabId, completedTabs)) {
    return 'next';
  }

  return 'pending';
};
